// Wallet-free public chain read for a video CONTAINER — the append-only list
// of published versions — and the one question a viewer needs answered about
// it: is the version I am watching the current one?
//
// Same boundary as useChainRecord: the API says where to look (a container id
// and the authority its PDA derives from), the chain says what is true. The
// answer is deliberately never stronger than the evidence — "the chain makes
// no claim about this" is one of the four outcomes, not an error.
//
// NOTHING HERE CAN STOP PLAYBACK, and that is structural rather than
// remembered. `VersionConsistency` is not a member of `VerificationOutcome`,
// so there is no call that could hand it to `applyVerificationOutcome` at all;
// it reaches the policy through `withVersionConsistency`, which touches one
// field and never `halt`. The reason is the plan's: the bytes of a superseded
// version hashed correctly against the root the publisher sealed, so they are
// authentic. Only bytes that hash wrong stop a player.
import { Buffer } from 'buffer'
import type { PublicKey } from '@solana/web3.js'
import { useMemo } from 'react'
import { useForumContainer } from '../solana/forum/index.js'
import type { VersionRefAccount, VideoContainerAccount } from '../solana/forum/index.js'

/** One version as the chain holds it, projected into the strings and Dates a
 * renderer can use. `videoPda` is the Video account this version's own root
 * hash lives on — the thing a viewer following the trail would read next. */
export interface ContainerVersionRecord {
  index: number
  videoId: string
  videoPda: string
  addedAt: Date
}

export interface ContainerRecord {
  containerPda: string
  /** What the container account itself claims. Deliberately kept alongside
   * `versions.length` rather than replaced by it: a ref that failed to decode
   * leaves the two disagreeing, and that disagreement is information. */
  versionCount: number
  /** Ascending by index. */
  versions: ContainerVersionRecord[]
}

/**
 * Where the version being played sits in the chain's list.
 *
 *   consistent  — it is the tail ref, index versionCount - 1
 *   superseded  — the chain lists a later version than this one
 *   unlisted    — the chain lists versions and this is none of them
 *   unanchored  — no container account exists; today's behaviour, not a fault
 *   unknown     — the read has not come back, failed, or the chain names no
 *                 current version to be absent from
 *
 * `unlisted` and `unknown` are separate values because they are separate
 * facts, and collapsing them was a real hole: an answer from the chain that
 * contradicts what the API served rendered identically to no answer at all —
 * that is, silently. See `panelWarning`.
 */
export type VersionConsistency = 'consistent' | 'superseded' | 'unlisted' | 'unanchored' | 'unknown'

/** The subset of a decoded VideoContainer that the projection reads. Narrower
 * than VideoContainerAccount on purpose, exactly as ChainRecordSource is:
 * a fixture supplies one field rather than fabricating a whole account. A
 * real VideoContainerAccount satisfies it structurally, no cast needed. */
export type ContainerRecordSource = Pick<VideoContainerAccount, 'versionCount'>

/** Likewise for a decoded VersionRef. `container` and `bump` are not read
 * here: the container is the account we derived the ref from, so echoing it
 * back proves nothing. */
export type VersionRefSource = Pick<VersionRefAccount, 'index' | 'videoId' | 'video' | 'addedAt'>

/** Reverse of `videoIdBytes` (solana/forum/encoding.ts): the program stores a
 * video id as its raw 16 bytes, and a page compares it against the hyphenated
 * UUID the API uses. Lowercase, because that is the only casing the API ever
 * mints and `computeVersionConsistency` normalizes anyway. */
export function videoIdFromBytes(bytes: number[] | Uint8Array): string {
  const hex = Buffer.from(bytes).toString('hex')
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20, 32)].join('-')
}

/**
 * Computes a ContainerRecord from a decoded container account and whatever
 * VersionRef accounts were readable under it. Pure and exported for the same
 * reason `computeChainRecord` is: the rule this ticket is TDD'd against has to
 * be testable without a validator, a wallet, or a React render.
 *
 * Sorts by index rather than trusting arrival order — the refs are fetched in
 * one batched request whose ordering is the caller's, not the chain's.
 */
export function computeContainerRecord(
  containerPda: PublicKey,
  container: ContainerRecordSource,
  versions: VersionRefSource[],
): ContainerRecord {
  return {
    containerPda: containerPda.toBase58(),
    versionCount: container.versionCount,
    versions: versions
      .map((ref) => ({
        index: ref.index,
        videoId: videoIdFromBytes(ref.videoId),
        videoPda: ref.video.toBase58(),
        addedAt: new Date(ref.addedAt.toNumber() * 1000),
      }))
      .sort((a, b) => a.index - b.index),
  }
}

const sameVideo = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase()

/**
 * The viewer surface's version axis, as a pure function.
 *
 * `missing` is the read's own distinction between "this container does not
 * exist on chain" and "we do not have an answer" — an RPC failure, a
 * derivation that could not be made, or a read still in flight. Collapsing
 * those two into one null would let a network blip render as "never
 * anchored", which is a claim about the publisher rather than about us.
 *
 * The tail is decided by `versionCount - 1`, the chain's own claim, not by
 * `versions.length - 1`. A ref that failed to decode must not promote the
 * version before it into looking current.
 */
export function computeVersionConsistency(
  record: ContainerRecord | null,
  missing: boolean,
  playingVideoId: string,
): VersionConsistency {
  if (missing) return 'unanchored'
  if (!record) return 'unknown'
  // The chain names no current version, so there is no list to be absent
  // from and nothing to tell a viewer.
  if (record.versionCount <= 0) return 'unknown'

  const playing = record.versions.find((version) => sameVideo(version.videoId, playingVideoId))
  if (!playing) {
    // The container account and its VersionRefs are two separate fetches
    // (useForumContainer), so there is a real render where versionCount is
    // already known and versions is still []. If the refs simply have not
    // come back yet — or the one read that mattered failed to decode — that
    // is a gap in OUR read, not a fact about the chain's list, and reporting
    // it as `unlisted` would flash an accusatory warning on every ordinary
    // page load. Only once every entry the chain claims has actually been
    // read, and the version playing is still absent from that complete set,
    // is "the chain lists versions and this is none of them" an ANSWER this
    // axis can stand behind — and it is the exact answer it was built to
    // surface: not 'superseded' (a version the chain has never heard of is
    // not an older version), and not 'unknown' either, because a viewer is
    // entitled to be told, without accusing anyone. See `panelWarning`.
    return record.versions.length < record.versionCount ? 'unknown' : 'unlisted'
  }

  return playing.index === record.versionCount - 1 ? 'consistent' : 'superseded'
}

export interface ContainerReadState {
  /** null while loading, when the read failed, or when the container was
   * never anchored — `missing` is what separates the last from the others. */
  record: ContainerRecord | null
  missing: boolean
  loading: boolean
  /** Whichever of the two chain reads actually failed: the container
   * account's own read error if that one failed, otherwise the refs read's —
   * exactly `containerState.error ?? versionsState.error` as
   * useForumContainer computes it. A caller that wants "an incomplete read
   * must never look like a disagreement" for a read that has actively
   * failed — rather than one still in flight — reads this rather than
   * inferring it from `record.versions.length`. */
  error: Error | null
}

/**
 * Reads a container's chain record with no wallet and no account, so a
 * stranger can check for themselves whether the version they were served is
 * the current one.
 *
 * Both arguments are optional because both are pointers the API supplies and
 * either can be absent: a video with no container id (nothing published yet)
 * and a video with no recorded authority both leave nothing to derive from.
 * That is `unknown`, never `unanchored` — we did not look, so we did not find
 * an empty chain.
 */
export function useContainerRecord(containerId: string | undefined, authority: string | undefined): ContainerReadState {
  // `useForumContainer` refuses a non-UUID container id by deriving null
  // rather than throwing, which is exactly the degradation an absent id wants.
  const { containerPda, container, versions, missing, loading, error } = useForumContainer(
    containerId ?? '',
    authority ?? null,
  )

  const record = useMemo(
    () =>
      containerPda && container
        ? computeContainerRecord(
            containerPda,
            container,
            versions.map((v) => v.ref),
          )
        : null,
    [containerPda, container, versions],
  )

  return { record, missing, loading, error }
}
